// app/components/Plugins/Automations/nodes/IndicatorNode.tsx
'use client'

import React, { useState, useCallback, useEffect } from 'react'
import { Handle, Position } from '@xyflow/react'
import { IndicatorNodeData, NodeProps } from '../types'

const INDICATORS = ['RSI', 'MACD', 'SMA', 'EMA', 'Bollinger Bands', 'Price']
const TIMEFRAMES = ['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w']
const CONDITIONS = [
  'crosses_above',
  'crosses_below',
  'is_above',
  'is_below',
  'is_increasing',
  'is_decreasing'
]

// Default parameters for each indicator type
const DEFAULT_PARAMS = {
  RSI: { period: 14 },
  MACD: { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 },
  SMA: { period: 20 },
  EMA: { period: 20 },
  'Bollinger Bands': { period: 20, stdDev: 2 },
  'Price': { priceType: 'close' }
}

const IndicatorNode: React.FC<NodeProps<IndicatorNodeData>> = ({ data, isConnectable }) => {
  // Main indicator settings
  const [indicator, setIndicator] = useState(data.indicator || 'RSI')
  const [timeframe, setTimeframe] = useState(data.timeframe || '1d')
  const [condition, setCondition] = useState(data.condition || 'crosses_above')
  const [value, setValue] = useState(data.value || '70')
  
  // Parameters for different indicator types
  const [rsiPeriod, setRsiPeriod] = useState(data.rsiPeriod || DEFAULT_PARAMS.RSI.period)
  const [smaPeriod, setSmaPeriod] = useState(data.smaPeriod || DEFAULT_PARAMS.SMA.period)
  const [emaPeriod, setEmaPeriod] = useState(data.emaPeriod || DEFAULT_PARAMS.EMA.period)
  const [macdFastPeriod, setMacdFastPeriod] = useState(data.macdFastPeriod || DEFAULT_PARAMS.MACD.fastPeriod)
  const [macdSlowPeriod, setMacdSlowPeriod] = useState(data.macdSlowPeriod || DEFAULT_PARAMS.MACD.slowPeriod)
  const [macdSignalPeriod, setMacdSignalPeriod] = useState(data.macdSignalPeriod || DEFAULT_PARAMS.MACD.signalPeriod)
  const [bbPeriod, setBbPeriod] = useState(data.bbPeriod || DEFAULT_PARAMS['Bollinger Bands'].period)
  const [bbStdDev, setBbStdDev] = useState(data.bbStdDev || DEFAULT_PARAMS['Bollinger Bands'].stdDev)
  const [priceType, setPriceType] = useState(data.priceType || DEFAULT_PARAMS['Price'].priceType)
  
  // Update data object with all parameters
  useEffect(() => {
    data.rsiPeriod = rsiPeriod
    data.smaPeriod = smaPeriod
    data.emaPeriod = emaPeriod
    data.macdFastPeriod = macdFastPeriod
    data.macdSlowPeriod = macdSlowPeriod
    data.macdSignalPeriod = macdSignalPeriod
    data.bbPeriod = bbPeriod
    data.bbStdDev = bbStdDev
    data.priceType = priceType
  }, [data, rsiPeriod, smaPeriod, emaPeriod, macdFastPeriod, macdSlowPeriod, macdSignalPeriod, bbPeriod, bbStdDev, priceType])
  
  const handleIndicatorChange = useCallback(
    (evt: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = evt.target.value
      setIndicator(newValue)
      data.indicator = newValue
      
      // Set appropriate default value based on indicator
      switch (newValue) {
        case 'RSI':
          setValue('70')
          data.value = '70'
          break
        case 'MACD':
          setValue('0')
          data.value = '0'
          break
        case 'SMA':
        case 'EMA':
          setValue('0')
          data.value = '0'
          break
        case 'Bollinger Bands':
          setValue('upper')
          data.value = 'upper'
          break
        case 'Price':
          setValue('100')
          data.value = '100'
          break
      }
    },
    [data]
  )
  
  const handleTimeframeChange = useCallback(
    (evt: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = evt.target.value
      setTimeframe(newValue)
      data.timeframe = newValue
    },
    [data]
  )
  
  const handleConditionChange = useCallback(
    (evt: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = evt.target.value
      setCondition(newValue)
      data.condition = newValue
    },
    [data]
  )
  
  const handleValueChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = evt.target.value
      setValue(newValue)
      data.value = newValue
    },
    [data]
  )
  
  const handleNumberChange = useCallback(
    (setter: React.Dispatch<React.SetStateAction<number>>, min: number = 1) => 
      (evt: React.ChangeEvent<HTMLInputElement>) => {
        const val = parseInt(evt.target.value)
        if (!isNaN(val) && val >= min) {
          setter(val)
        }
      },
    []
  )
  
  // Render parameter inputs based on selected indicator
  const renderParameterInputs = () => {
    switch (indicator) {
      case 'RSI':
        return (
          <label>
            <div className="label-text">Period</div>
            <input 
              type="number" 
              value={rsiPeriod} 
              onChange={handleNumberChange(setRsiPeriod)}
              min="1"
              className="node-input"
            />
          </label>
        )
      case 'MACD':
        return (
          <>
            <label>
              <div className="label-text">Fast Period</div>
              <input 
                type="number" 
                value={macdFastPeriod} 
                onChange={handleNumberChange(setMacdFastPeriod)}
                min="1"
                className="node-input"
              />
            </label>
            <label>
              <div className="label-text">Slow Period</div>
              <input 
                type="number" 
                value={macdSlowPeriod} 
                onChange={handleNumberChange(setMacdSlowPeriod)}
                min="1"
                className="node-input"
              />
            </label>
            <label>
              <div className="label-text">Signal Period</div>
              <input 
                type="number" 
                value={macdSignalPeriod} 
                onChange={handleNumberChange(setMacdSignalPeriod)}
                min="1"
                className="node-input"
              />
            </label>
          </>
        )
      case 'SMA':
        return (
          <label>
            <div className="label-text">Period</div>
            <input 
              type="number" 
              value={smaPeriod} 
              onChange={handleNumberChange(setSmaPeriod)}
              min="1"
              className="node-input"
            />
          </label>
        )
      case 'EMA':
        return (
          <label>
            <div className="label-text">Period</div>
            <input 
              type="number" 
              value={emaPeriod} 
              onChange={handleNumberChange(setEmaPeriod)}
              min="1"
              className="node-input"
            />
          </label>
        )
      case 'Bollinger Bands':
        return (
          <>
            <label>
              <div className="label-text">Period</div>
              <input 
                type="number" 
                value={bbPeriod} 
                onChange={handleNumberChange(setBbPeriod)}
                min="1"
                className="node-input"
              />
            </label>
            <label>
              <div className="label-text">Standard Deviation</div>
              <input 
                type="number" 
                value={bbStdDev} 
                onChange={handleNumberChange(setBbStdDev, 0.1)}
                min="0.1"
                step="0.1"
                className="node-input"
              />
            </label>
            <label>
              <div className="label-text">Band</div>
              <select
                value={value}
                onChange={(e) => {
                  setValue(e.target.value)
                  data.value = e.target.value
                }}
                className="node-select"
              >
                <option value="upper">Upper Band</option>
                <option value="middle">Middle Band</option>
                <option value="lower">Lower Band</option>
              </select>
            </label>
          </>
        )
      case 'Price':
        return (
          <label>
            <div className="label-text">Price Type</div>
            <select
              value={priceType}
              onChange={(e) => {
                setPriceType(e.target.value)
                data.priceType = e.target.value
              }}
              className="node-select"
            >
              <option value="close">Close</option>
              <option value="open">Open</option>
              <option value="high">High</option>
              <option value="low">Low</option>
              <option value="volume">Volume</option>
            </select>
          </label>
        )
      default:
        return null
    }
  }
  
  // Render value input based on selected indicator
  const renderValueInput = () => {
    if (indicator === 'Bollinger Bands') {
      return null // Value is handled in parameter inputs for Bollinger Bands
    }
    
    // For Price indicator, add a label to clarify what the value means
    if (indicator === 'Price') {
      return (
        <label>
          <div className="label-text">Threshold Value</div>
          <input 
            type="text" 
            value={value} 
            onChange={handleValueChange} 
            className="node-input"
          />
          <div className="label-text" style={{ fontSize: '11px', color: '#aaa' }}>
            Compare {priceType} price against this value
          </div>
        </label>
      )
    }
    
    // Special handling for MACD indicator
    if (indicator === 'MACD') {
      const isAboveOrBelow = condition === 'is_above' || condition === 'is_below'
      
      if (isAboveOrBelow) {
        // Show normal value input for is_above and is_below conditions
        return (
          <label>
            <div className="label-text">Value</div>
            <input 
              type="text" 
              value={value} 
              onChange={handleValueChange} 
              className="node-input"
            />
          </label>
        )
      } else {
        // Show disabled input with appropriate text for crossing conditions
        const displayValue = condition.includes('crosses') ? 'Signal Value' : 'Histogram Value'
        return (
          <label>
            <div className="label-text">Value</div>
            <input 
              type="text" 
              value={displayValue}
              disabled
              className="node-input"
              style={{ backgroundColor: '#f5f5f5', color: '#666' }}
            />
          </label>
        )
      }
    }
    
    // Special handling for SMA indicator
    if (indicator === 'SMA') {
      const isAboveOrBelow = condition === 'is_above' || condition === 'is_below'
      
      if (isAboveOrBelow) {
        // Show normal value input for is_above and is_below conditions
        return (
          <label>
            <div className="label-text">Value</div>
            <input 
              type="text" 
              value={value} 
              onChange={handleValueChange} 
              className="node-input"
            />
          </label>
        )
      } else {
        // Show disabled input for crossing conditions
        const displayValue = condition.includes('crosses') ? 'Price Value' : 'SMA Value'
        return (
          <label>
            <div className="label-text">Value</div>
            <input 
              type="text" 
              value={displayValue}
              disabled
              className="node-input"
              style={{ backgroundColor: '#f5f5f5', color: '#666' }}
            />
          </label>
        )
      }
    }
    
    // Special handling for EMA indicator
    if (indicator === 'EMA') {
      const isAboveOrBelow = condition === 'is_above' || condition === 'is_below'
      
      if (isAboveOrBelow) {
        // Show normal value input for is_above and is_below conditions
        return (
          <label>
            <div className="label-text">Value</div>
            <input 
              type="text" 
              value={value} 
              onChange={handleValueChange} 
              className="node-input"
            />
          </label>
        )
      } else {
        // Show disabled input for crossing conditions
        const displayValue = condition.includes('crosses') ? 'Price Value' : 'EMA Value'
        return (
          <label>
            <div className="label-text">Value</div>
            <input 
              type="text" 
              value={displayValue}
              disabled
              className="node-input"
              style={{ backgroundColor: '#f5f5f5', color: '#666' }}
            />
          </label>
        )
      }
    }
    
    return (
      <label>
        <div className="label-text">Value</div>
        <input 
          type="text" 
          value={value} 
          onChange={handleValueChange} 
          className="node-input"
        />
      </label>
    )
  }
  
  return (
    <div className="node indicatorNode">
      <div className="node-header">Indicator</div>
      <div className="node-content">
        <label>
          <div className="label-text">Indicator</div>
          <select 
            value={indicator} 
            onChange={handleIndicatorChange}
            className="node-select"
          >
            {INDICATORS.map(ind => (
              <option key={ind} value={ind}>{ind}</option>
            ))}
          </select>
        </label>
        
        {renderParameterInputs()}
        
        <label>
          <div className="label-text">Timeframe</div>
          <select 
            value={timeframe} 
            onChange={handleTimeframeChange}
            className="node-select"
          >
            {TIMEFRAMES.map(tf => (
              <option key={tf} value={tf}>{tf}</option>
            ))}
          </select>
        </label>
        
        <label>
          <div className="label-text">Condition</div>
          <select 
            value={condition} 
            onChange={handleConditionChange}
            className="node-select"
          >
            {CONDITIONS.map(cond => (
              <option key={cond} value={cond}>
                {cond.replace('_', ' ')}
              </option>
            ))}
          </select>
        </label>
        
        {renderValueInput()}
      </div>
      
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="in"
        isConnectable={isConnectable}
      />
      
      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="out"
        isConnectable={isConnectable}
      />
    </div>
  )
}

export default IndicatorNode